import React, { useCallback, useEffect, useState } from "react";
import {
  Check, Download, FileText, KeyRound, LifeBuoy, Loader2, Monitor, Moon,
  Plus, ShieldAlert, Sun, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useLLM } from "@/contexts/LLMContext";
import { useTheme } from "@/contexts/ThemeContext";
import { account, legal as legalApi, llm, normalizeError, sessionCredentials } from "@/lib/api";
import LegalDialog from "@/components/LegalDialog";
import SupportDialog from "@/components/SupportDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PROJECT_LABELS } from "@/lib/projectTypes";
import { toast } from "sonner";
import AiBadge from "@/components/AiBadge";

const ROLES = [
  { id: "student", label: "Studente" },
  { id: "researcher", label: "Ricercatore" },
  { id: "faculty", label: "Docente" },
];

const THEMES = [
  { id: "light", label: "Chiaro", icon: Sun },
  { id: "dark", label: "Scuro", icon: Moon },
  { id: "system", label: "Sistema", icon: Monitor },
];

function Section({ title, description, children }) {
  return (
    <section className="border-t border-line py-8 first:border-t-0 first:pt-0">
      <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
        <div>
          <h2 className="font-serif-display text-[17px] font-semibold text-ink">{title}</h2>
          {description && <p className="numa-meta mt-1 leading-relaxed">{description}</p>}
        </div>
        <div className="min-w-0">{children}</div>
      </div>
    </section>
  );
}

function ModelKeys() {
  const {refresh, openKeyDialog, status, showError } = useLLM();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const sessionKey = sessionCredentials.get();

  const load = useCallback(async () => {
    try {
      const data = await llm.keys();
      setKeys(data.keys || []);
    } catch {
      setKeys([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, status.provider, status.ready]);

  const activate = async (provider) => {
    setBusy(provider);
    try {
      await llm.activate(provider);
      await Promise.all([load(), refresh()]);
    } catch (err) {
      showError(normalizeError(err));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (provider) => {
    setBusy(provider);
    try {
      await llm.removeKey(provider);
      await Promise.all([load(), refresh()]);
      toast.success("Chiave rimossa.");
    } catch (err) {
      showError(normalizeError(err));
    } finally {
      setBusy(null);
    }
  };

  const forgetSession = () => {
    sessionCredentials.clear();
    refresh();
    toast.success("Chiave di sessione dimenticata.");
  };

  if (loading) {
    return <div className="numa-shimmer h-20 rounded-md" />;
  }

  return (
    <div className="space-y-3">
      {sessionKey?.api_key && (
        <div className="numa-panel-assistant flex items-center gap-3 p-3">
          <div className="min-w-0 flex-1">
            <p className="text-[13.5px] font-medium text-ink">
              Chiave di sessione attiva · {sessionKey.provider}
            </p>
            <p className="mt-0.5 text-2xs text-ink-muted">
              Non salvata sul server. Sparisce chiudendo questa scheda.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={forgetSession}>
            Dimentica
          </Button>
        </div>
      )}

      {keys.length === 0 && !sessionKey?.api_key && (
        <div className="numa-panel-quiet p-4 text-center">
          <p className="text-[13.5px] text-ink">Nessun modello collegato.</p>
          <p className="numa-meta mt-1">
            Le funzioni di revisione, i riassunti delle fonti e la chat di impostazione
            richiedono un account collegato o una API key. Il resto di NUMA funziona senza.
          </p>
        </div>
      )}

      <ul className="space-y-2">
        {keys.map((k) => (
          <li
            key={k.provider}
            className="flex flex-wrap items-center gap-3 rounded-md border border-line bg-surface px-3.5 py-3"
            data-testid={`llm-key-${k.provider}`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="text-[13.5px] font-medium text-ink">{k.label}</p>
                {k.active && (
                  <span className="numa-tag">
                    <Check className="h-3 w-3" /> in uso
                  </span>
                )}
              </div>
              <p className="mt-0.5 font-mono text-2xs text-ink-subtle">
                {k.masked} · {k.model}
                {k.base_url ? ` · ${k.base_url}` : ""}
              </p>
              {k.connection === "account" && (
                <p className="mt-1 inline-flex items-center gap-1 text-2xs text-ok">
                  <Check className="h-3 w-3" /> collegato tramite account
                </p>
              )}
            </div>
            <div className="flex gap-1.5">
              {!k.active && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy === k.provider}
                  onClick={() => activate(k.provider)}
                >
                  {busy === k.provider ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  Usa questo
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={busy === k.provider}
                onClick={() => remove(k.provider)}
                title={`Rimuovi la chiave ${k.label}`}
              >
                <Trash2 className="h-3.5 w-3.5 text-ink-muted" />
                <span className="sr-only">Rimuovi {k.label}</span>
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <Button variant="outline" size="sm" onClick={() => openKeyDialog()} data-testid="add-key-btn">
        <Plus className="h-3.5 w-3.5" /> Collega un account o una chiave
      </Button>

      <p className="numa-meta leading-relaxed">
        <span className="font-medium text-ink">Ottenere una chiave è gratuito.</span> Non è un
        abbonamento: il provider addebita solo il testo effettivamente elaborato, e solo quando
        usi una funzione assistita. Le chiavi salvate sono cifrate con AES-256-GCM e una
        sottochiave derivata dal tuo account: non vengono mai restituite in chiaro al browser
        e non sono leggibili da un altro utente.
      </p>
    </div>
  );
}

function ProfileForm() {
  const { showError } = useLLM();
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [title, setTitle] = useState(user?.title || "");
  const [role, setRole] = useState(user?.role || "researcher");
  const [saving, setSaving] = useState(false);

  const dirty = name !== (user?.name || "") || title !== (user?.title || "") || role !== (user?.role || "researcher");

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { auth } = await import("@/lib/api");
      const updated = await auth.updateProfile({ name, title: title || null, role });
      setUser(updated);
      toast.success("Profilo aggiornato.");
    } catch (err) {
      showError(normalizeError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-md space-y-4">
      <div className="grid gap-3 sm:grid-cols-[110px_1fr]">
        <div className="space-y-1.5">
          <Label htmlFor="profile-title">Titolo</Label>
          <Input
            id="profile-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Prof.ssa"
            className="h-10"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="profile-name">Nome</Label>
          <Input id="profile-name" value={name} onChange={(e) => setName(e.target.value)} className="h-10" />
        </div>
      </div>

      <fieldset>
        <legend className="mb-1.5 text-sm font-medium text-ink">Ruolo</legend>
        <div className="grid grid-cols-3 gap-1.5">
          {ROLES.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setRole(r.id)}
              aria-pressed={role === r.id}
              className={`rounded-md border px-2 py-2 text-[13px] transition-colors ${
                role === r.id
                  ? "border-brand bg-brand-soft font-medium text-brand"
                  : "border-line-strong text-ink-muted hover:bg-surface-2"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="space-y-1.5">
        <Label>Email</Label>
        <p className="rounded-md border border-line bg-surface-2 px-3 py-2 text-[13.5px] text-ink-muted">
          {user?.email}
        </p>
      </div>

      <Button type="submit" size="sm" disabled={!dirty || saving}>
        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Salva
      </Button>
    </form>
  );
}


/**
 * The GDPR rights a user should never have to send an email to exercise:
 * access and portability as a download, erasure as a button.
 */
function DataAndLegal() {
  const { showError } = useLLM();
  const { logout } = useAuth();
  const [owner, setOwner] = useState(null);
  const [legalDoc, setLegalDoc] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    legalApi.owner().then(setOwner).catch(() => setOwner(null));
  }, []);

  const exportData = async () => {
    setBusy("export");
    try {
      const data = await account.exportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `numa-dati-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Esportazione scaricata.");
    } catch (err) {
      showError(normalizeError(err));
    } finally {
      setBusy("");
    }
  };

  const deleteAccount = async () => {
    setBusy("delete");
    try {
      await account.remove();
      toast.success("Account eliminato.");
      await logout();
    } catch (err) {
      showError(normalizeError(err));
    } finally {
      setBusy("");
      setConfirmDelete(false);
    }
  };

  return (
    <>
      <Section
        title="I tuoi dati"
        description="Puoi portarli via o cancellarli in qualsiasi momento, senza doverci scrivere."
      >
        <div className="max-w-xl space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-surface px-3.5 py-3">
            <div className="min-w-0">
              <p className="text-[13.5px] font-medium text-ink">Scarica tutti i tuoi dati</p>
              <p className="numa-meta mt-0.5">
                Progetti, versioni, fonti e bibliografia in un file JSON.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={exportData} disabled={busy === "export"}>
              {busy === "export" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              Esporta
            </Button>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-danger/30 bg-danger/5 px-3.5 py-3">
            <div className="min-w-0">
              <p className="text-[13.5px] font-medium text-ink">Elimina l&apos;account</p>
              <p className="numa-meta mt-0.5">
                Cancella definitivamente account, progetti, fonti e chiavi. L'operazione è irreversibile.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setConfirmDelete(true)}>
              <ShieldAlert className="h-3.5 w-3.5" /> Elimina
            </Button>
          </div>
        </div>
      </Section>

      <Section title="Assistenza" description="Scrivici per un problema, un suggerimento o una richiesta sui tuoi dati.">
        <div className="max-w-xl space-y-2.5">
          <Button variant="outline" size="sm" onClick={() => setSupportOpen(true)} data-testid="open-support">
            <LifeBuoy className="h-3.5 w-3.5" /> Contatta l&apos;assistenza
          </Button>
          {owner?.support_email && (
            <p className="numa-meta">
              Oppure scrivi direttamente a{" "}
              <a href={`mailto:${owner.support_email}`} className="text-brand hover:underline">
                {owner.support_email}
              </a>
              .
            </p>
          )}
        </div>
      </Section>

      <Section title="Note legali" description="Chi gestisce il servizio e a quali condizioni.">
        <div className="max-w-xl space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setLegalDoc("privacy")}>
              <FileText className="h-3.5 w-3.5" /> Informativa privacy
            </Button>
            <Button variant="outline" size="sm" onClick={() => setLegalDoc("terms")}>
              <FileText className="h-3.5 w-3.5" /> Condizioni d&apos;uso
            </Button>
          </div>

          {owner && (
            <dl className="space-y-1.5 border-t border-line pt-3 text-[13px]">
              <div className="flex justify-between gap-4">
                <dt className="text-ink-muted">Titolare del trattamento</dt>
                <dd className="text-right text-ink">
                  {owner.configured ? owner.name : "non ancora configurato"}
                </dd>
              </div>
              {owner.vat && (
                <div className="flex justify-between gap-4">
                  <dt className="text-ink-muted">P. IVA / C.F.</dt>
                  <dd className="text-right tabular-nums text-ink">{owner.vat}</dd>
                </div>
              )}
              <div className="flex justify-between gap-4">
                <dt className="text-ink-muted">Versione informativa</dt>
                <dd className="text-right tabular-nums text-ink">{owner.privacy_version}</dd>
              </div>
            </dl>
          )}
        </div>
      </Section>

      <LegalDialog
        open={Boolean(legalDoc)}
        onOpenChange={(o) => !o && setLegalDoc(null)}
        document={legalDoc || "privacy"}
      />
      <SupportDialog
        open={supportOpen}
        onOpenChange={setSupportOpen}
        supportEmail={owner?.support_email}
      />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display">Eliminare definitivamente l&apos;account?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno cancellati tutti i tuoi progetti, lo storico delle versioni, le fonti
              caricate, la bibliografia e le chiavi API salvate. L&apos;operazione non è
              reversibile. Se vuoi conservare il lavoro, esporta prima i tuoi dati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteAccount}
              disabled={busy === "delete"}
              className="bg-danger text-on-solid hover:bg-danger/90"
            >
              Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default function SettingsView({ thesis }) {
  const { mode, setMode } = useTheme();

  return (
    <div className="h-screen overflow-y-auto bg-paper numa-fade-in" data-testid="settings-view">
      <div className="mx-auto max-w-4xl px-6 py-9 lg:px-10">
        <header className="mb-8">
          <h1 className="font-serif-display text-[28px] font-semibold text-ink">Impostazioni</h1>
          <AiBadge mode="none" className="mt-3" />
          <p className="numa-meta mt-1">Modello, profilo e aspetto dell'interfaccia.</p>
        </header>

        <Section
          title="Modelli e chiavi"
          description="Collega un account o una chiave. NUMA non rivende accesso ai modelli: il consumo è fatturato dal tuo provider, al costo reale."
        >
          <ModelKeys />
        </Section>

        <Section title="Profilo" description="Il titolo compare nei saluti e nell'intestazione del workspace.">
          <ProfileForm />
        </Section>

        <Section title="Aspetto" description="Il tema segue il sistema se non scegli diversamente.">
          <div className="flex gap-1.5">
            {THEMES.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setMode(id)}
                aria-pressed={mode === id}
                data-testid={`theme-${id}`}
                className={`flex items-center gap-2 rounded-md border px-3 py-2 text-[13px] transition-colors ${
                  mode === id
                    ? "border-brand bg-brand-soft font-medium text-brand"
                    : "border-line-strong text-ink-muted hover:bg-surface-2"
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>
        </Section>

        {thesis && (
          <Section title="Progetto attivo" description="Riepilogo del documento aperto.">
            <dl className="max-w-md space-y-2.5">
              <div className="flex justify-between gap-4 border-b border-line pb-2">
                <dt className="numa-meta">Titolo</dt>
                <dd className="text-right text-[13.5px] text-ink">{thesis.title}</dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-line pb-2">
                <dt className="numa-meta">Tipo</dt>
                <dd className="text-right text-[13.5px] text-ink">
                  {PROJECT_LABELS[thesis.project_type] || "Documento"}
                </dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-line pb-2">
                <dt className="numa-meta">Sezioni</dt>
                <dd className="text-right text-[13.5px] tabular-nums text-ink">{thesis.chapters?.length || 0}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="numa-meta">Avanzamento</dt>
                <dd className="text-right text-[13.5px] tabular-nums text-ink">
                  {thesis.completion_percentage || 0}%
                </dd>
              </div>
            </dl>
          </Section>
        )}

        <DataAndLegal />

        <Section title="Privacy" description="Cosa lascia il tuo dispositivo, e cosa no.">
          <ul className="max-w-xl space-y-2.5 text-[13.5px] leading-relaxed text-ink-muted">
            <li className="flex gap-2.5">
              <KeyRound className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-subtle" />
              I testi vengono inviati solo al provider di cui hai fornito la chiave, al
              momento in cui avvii una funzione assistita.
            </li>
            <li className="flex gap-2.5">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ok" />
              NUMA non esegue analytics di terze parti e non registra le sessioni di
              scrittura.
            </li>
          </ul>
        </Section>
      </div>
    </div>
  );
}
